import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import PackageToolbar from "@/components/packages/PackageToolbar";
import PackageTable from "@/components/packages/PackageTable";
import PackageHeader from "@/components/packages/PackageHeader";
import { usePackages, useUpdatePackage, useDeletePackage } from '@/hooks/usePackages';
import NewPackageModal from "@/components/packages/NewPackageModal";
import SignatureModal from "@/components/packages/SignatureModal";
import PackageInfoModal from "@/components/packages/PackageInfoModal";
import PackageHistoryModal from "@/components/packages/PackageHistoryModal";
import DeletePackageDialog from "@/components/packages/DeletePackageDialog";
import DiscardPackageDialog from "@/components/packages/DiscardPackageDialog";
import GenerateReportModal from "@/components/packages/GenerateReportModal";
import EditPackageModal from "@/components/packages/EditPackageModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { Package } from "@/types";
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { isPackageOlderThan30Days } from "@/lib/utils";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext
} from "@/components/ui/pagination";

const PackageLog = () => {
  // State for tracking modal visibility
  const [newPackageModalOpen, setNewPackageModalOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 60;
  
  const isMobile = useIsMobile();
  
  // Supabase hooks
  const { packages, isLoading, createPackage, error, refetchPackages } = usePackages();
  const updatePackage = useUpdatePackage();
  const deletePackageMutation = useDeletePackage();
  const { user } = useSupabaseAuth();
  
  // Filter/search/sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<'All' | string>('All');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(1); // Reset page when filters/search change
  }, [searchQuery, statusFilter, startDate, endDate]);

  const filteredPackages = useMemo(() => {
    if (!packages) return [];
    return packages.map(pkg => {
      // Normalize observations
      let obs = { oversized: false, overweight: false };
      if (
        pkg.observations &&
        typeof pkg.observations === 'object' &&
        !Array.isArray(pkg.observations) &&
        'oversized' in pkg.observations &&
        'overweight' in pkg.observations
      ) {
        obs = {
          oversized: Boolean((pkg.observations as any).oversized),
          overweight: Boolean((pkg.observations as any).overweight)
        };
      }
      // Normalize history
      let hist = [];
      if (Array.isArray(pkg.history)) {
        hist = pkg.history
          .filter(h => typeof h === 'object' && h !== null && 'status' in h)
          .map(h => ({
            ...(h as object),
            status: (h as any).status as import("@/types").Status
          }));
      }
      return {
        ...pkg,
        status: pkg.status as import("@/types").Status,
        history: hist,
        observations: obs
      };
    }).filter(pkg => {
      // Search filter
      const matchesSearch =
        pkg.guest_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pkg.tracking.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (pkg.room_number || "").toLowerCase().includes(searchQuery.toLowerCase());
      // Status filter
      const matchesStatus = statusFilter === 'All' || pkg.status === statusFilter;
      // Date range filter
      let matchesDateRange = true;
      if (startDate) {
        matchesDateRange = matchesDateRange && new Date(pkg.entry_date) >= new Date(startDate);
      }
      if (endDate) {
        matchesDateRange = matchesDateRange && new Date(pkg.entry_date) <= new Date(endDate);
      }
      return matchesSearch && matchesStatus && matchesDateRange;
    }).sort((a, b) => b.sequential_number - a.sequential_number);
  }, [packages, searchQuery, statusFilter, startDate, endDate]);

  const totalPages = Math.ceil(filteredPackages.length / pageSize);
  const paginatedPackages = filteredPackages.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Effect to set grid view on mobile devices
  useEffect(() => {
    if (isMobile) {
      setViewMode("grid");
    }
  }, [isMobile]);

  // Open package info modal for editing
  const handleViewInfo = (pkg: Package) => {
    setSelectedPackage(pkg);
    setInfoModalOpen(true);
  };

  // Open history modal for viewing package timeline
  const handleViewHistory = (pkg: Package) => {
    setSelectedPackage(pkg);
    setHistoryModalOpen(true);
  };

  // Handle mark as delivered action
  const handleMarkDelivered = (pkg: Package) => {
    setSelectedPackage(pkg);
    setSignatureModalOpen(true);
  };

  // Confirm package delivery with signature
  const handleConfirmSignature = async (id: number, signature: string, receiver: string) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({
      id: String(id),
      changes: {
        status: "Delivered",
        signature,
        receiver_name: receiver,
        last_updated_by
      }
    });
    setSignatureModalOpen(false);
    setTimelineRefreshKey(prev => prev + 1);
    toast.success("Package marked as delivered.");
  };

  // Undo delivery
  const handleUndoDelivery = async (pkg: Package) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({
      id: String(pkg.id),
      changes: {
        status: "Pending",
        signature: null,
        receiver_name: null,
        last_updated_by
      }
    });
    setTimelineRefreshKey(prev => prev + 1);
    toast.success("Delivery reversed.");
  };

  // Open delete confirmation dialog
  const handleDelete = (pkg: Package) => {
    setSelectedPackage(pkg);
    setDeleteDialogOpen(true);
  };

  // Confirm package deletion
  const confirmDelete = async (id: number) => {
    const now = new Date().toISOString();
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({
      id: String(id),
      changes: {
        is_deleted: true,
        deletion_requested_at: now,
        status: "Deleted",
        last_updated_by
      }
    });
    setDeleteDialogOpen(false);
    setTimelineRefreshKey(prev => prev + 1);
    toast.success("Package marked for deletion. You can undo this action within 48 hours.");
  };

  // Undo soft-delete
  const handleUndoDelete = async (id: number) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({
      id: String(id),
      changes: {
        is_deleted: false,
        deletion_requested_at: null,
        status: "Pending",
        last_updated_by
      }
    });
    setTimelineRefreshKey(prev => prev + 1);
    toast.success("Package deletion undone.");
  };

  // Open discard confirmation dialog
  const handleDiscard = (pkg: Package) => {
    setSelectedPackage(pkg);
    setDiscardDialogOpen(true);
  };

  // Confirm package discard
  const confirmDiscard = async (id: number) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({
      id: String(id),
      changes: {
        status: "Discarded",
        last_updated_by
      }
    });
    setDiscardDialogOpen(false);
    setTimelineRefreshKey(prev => prev + 1);
    toast.success("Package marked as discarded. You can undo this action.");
  };

  // Undo discard
  const handleUndoDiscard = async (id: number) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({
      id: String(id),
      changes: {
        status: "Pending",
        last_updated_by
      }
    });
    setTimelineRefreshKey(prev => prev + 1);
    toast.success("Package discard undone.");
  };

  // Toggle view between list and grid
  const handleToggleView = (view: "list" | "grid") => {
    setViewMode(view);
  };

  // Handler for toggling observations
  const handleToggleObservation = (pkgId: number, observation: 'oversized' | 'overweight') => {
    const pkg = packages?.find(p => p.id === pkgId);
    if (!pkg) return;
    const newObservations = {
      ...(typeof pkg.observations === 'object' && pkg.observations !== null ? pkg.observations : {}),
      [observation]: !pkg.observations[observation]
    };
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    updatePackage.mutateAsync({ id: String(pkgId), changes: { observations: newObservations, last_updated_by } });
  };

  // Handler for adding a new package (sequential_number is assigned by backend)
  const handleAddPackage = async (pkg) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    // Remove sequential_number if present
    const { sequential_number, ...pkgToSend } = pkg;
    const finalPkg = { ...pkgToSend, last_updated_by };
    console.log("DEBUG: Package to send to Supabase:", finalPkg);
    await createPackage(finalPkg);
    setNewPackageModalOpen(false);
  };

  // Handler for updating a package
  const handleUpdatePackage = async (id, changes) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({ id, changes: { ...changes, last_updated_by } });
    setInfoModalOpen(false);
  };

  // Handler to open edit modal
  const handleEditPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setEditModalOpen(true);
  };

  // Handler to save edits
  const handleSaveEditPackage = async (id: number, changes: any) => {
    const last_updated_by = user?.user_metadata?.username || user?.email || 'unknown';
    await updatePackage.mutateAsync({ id: String(id), changes: { ...changes, last_updated_by } });
    setEditModalOpen(false);
    toast.success("Package updated successfully.");
  };

  if (error) {
    return (
      <div className="text-center text-red-500 flex flex-col items-center gap-2 mt-8">
        <span>{error.message}</span>
        <button
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          onClick={() => refetchPackages()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PackageHeader />
      
      <main className="container mx-auto p-4 flex-1">
        <PackageToolbar 
          onNewPackage={() => setNewPackageModalOpen(true)}
          onGenerateReport={() => setReportModalOpen(true)}
          view={viewMode}
          onToggleView={handleToggleView}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        
        <PackageTable
          packages={paginatedPackages}
          viewMode={viewMode}
          isLoading={isLoading}
          onMarkDelivered={handleMarkDelivered}
          onUndoDelivery={handleUndoDelivery}
          onViewInfo={handleViewInfo}
          onViewHistory={handleViewHistory}
          onDelete={handleDelete}
          onDiscard={handleDiscard}
          onToggleObservation={handleToggleObservation}
          onUndoDelete={handleUndoDelete}
          onUndoDiscard={handleUndoDiscard}
          onEdit={handleEditPackage}
        />
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    aria-disabled={currentPage === 1}
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <PaginationItem key={idx}>
                    <PaginationLink
                      isActive={currentPage === idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      style={{ cursor: 'pointer' }}
                    >
                      {idx + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    aria-disabled={currentPage === totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </main>
      
      {/* Modals */}
      <NewPackageModal
        open={newPackageModalOpen}
        onOpenChange={setNewPackageModalOpen}
        onSubmit={handleAddPackage}
      />
      
      <SignatureModal
        open={signatureModalOpen}
        onOpenChange={setSignatureModalOpen}
        onConfirm={handleConfirmSignature}
        packageId={selectedPackage?.id}
      />
      
      <PackageInfoModal
        key={timelineRefreshKey}
        open={infoModalOpen}
        onOpenChange={setInfoModalOpen}
        packageData={selectedPackage}
        onSave={handleUpdatePackage}
      />
      
      <PackageHistoryModal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        packageData={selectedPackage}
      />
      
      <DeletePackageDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => selectedPackage && confirmDelete(selectedPackage.id)}
      />
      
      <DiscardPackageDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onConfirm={() => selectedPackage && confirmDiscard(selectedPackage.id)}
        isBeforeThirtyDays={selectedPackage ? !isPackageOlderThan30Days(selectedPackage.entry_date) : false}
      />
      
      <GenerateReportModal
        open={reportModalOpen}
        onOpenChange={setReportModalOpen}
      />
      
      <EditPackageModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        packageData={selectedPackage}
        onSave={handleSaveEditPackage}
      />
    </div>
  );
};

export default PackageLog;
